import { useEffect, useRef, useState, useMemo } from "react";
import Media, { MediaProps } from "./Media";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Portal } from "@radix-ui/react-tooltip";
import { AdStatus } from "./types";
import { VideoRetentionData } from "@/screens/creatives/types";
import WinningStar from "@/components/common/icons/WinningStar";

export interface BodyProps {
  icon_src?: string;
  name?: string;
  description?: string;
  media: MediaProps & { videoRetentionData?: VideoRetentionData };
  winningCriteria?: { criteriaName: string; colorCode: string }[];
}
const Body = ({
  icon_src,
  name,
  description,
  media,
  status,
  processingText,
  processingMsg,
  winningCriteria,
}: BodyProps & {
  status?: AdStatus;
  processingText?: boolean;
  processingMsg?: string;
}) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    if (element) {
      setHasOverflow(element.scrollHeight > element.clientHeight);
    }
  }, [description]);

  // Calculate winning stars display
  const { displayedStars, remainingCount, winningCriteriaNames } = useMemo(() => {
    if (!winningCriteria || winningCriteria.length === 0) {
      return { displayedStars: [], remainingCount: 0, winningCriteriaNames: [] };
    }

    // Extract criteria names
    const criteriaNames = winningCriteria.map((item) => item.criteriaName);

    // Determine how many stars to display
    const totalStars = winningCriteria.length;
    const starsToShow = totalStars > 3 ? 2 : totalStars;

    // Create displayed stars with overlap
    const displayedStars = winningCriteria.slice(0, starsToShow).map((item, index) => (
      <div
        key={`${item.colorCode}-${index}`}
        style={{
          marginLeft: index > 0 ? "-6px" : "0",
        }}
      >
        <WinningStar color={item.colorCode} size={20} />
      </div>
    ));

    // Calculate remaining count
    const remainingCount = totalStars > 3 ? totalStars - 2 : 0;

    return { displayedStars, remainingCount, winningCriteriaNames: criteriaNames };
  }, [winningCriteria]);

  return (
    <>
      {((icon_src && name) || description) && (
        <div className="w-full flex flex-col gap-[8px] mb-[12px]">
          {icon_src && name && (
            <div className="flex gap-[6px]">
              <img
                src={icon_src}
                alt="platform icon"
                className="max-h-[13px] object-contain shrink-0"
              />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild className="cursor-default">
                    <span className="flex-1 min-w-0 truncate text-brand-primary-800 text-[12px] font-normal leading-[16px]">
                      {name}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent align="start" side="bottom">
                    <p>{name}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
          {description && (
            <div className="flex flex-col gap-[4px]">
              <p
                ref={textRef}
                className={`min-w-0 text-[rgba(2,24,89,0.80)] font-['Helvetica_Neue'] text-[12px] font-normal leading-[16px] ${!isExpanded ? "line-clamp-3" : ""}`}
              >
                {description}
              </p>
              {hasOverflow && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-[rgba(2,24,89,0.50)] text-[12px] font-normal leading-[16px] cursor-pointer w-fit"
                >
                  {isExpanded ? "Show less" : "Read more"}
                </button>
              )}
            </div>
          )}
        </div>
      )}
      <div className="relative w-full">
        {/* Winning stars annotation - positioned relative to media container */}
        {displayedStars.length > 0 && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="absolute top-[-8px] right-0 h-[24px] flex justify-end items-center cursor-pointer"
                  style={{ zIndex: 1 }}
                >
                  <div className="flex items-center">
                    {displayedStars}
                    {remainingCount > 0 && (
                      <div
                        className="flex items-center justify-center rounded-full"
                        style={{
                          width: "24px",
                          height: "24px",
                          border: "1px solid #CCD1DE",
                          background: "#FFF",
                          fontSize: "11px",
                          fontWeight: 500,
                          lineHeight: "normal",
                          color: "rgba(2, 24, 89, 0.80)",
                          marginLeft: displayedStars.length > 0 ? "-6px" : "0",
                        }}
                      >
                        +{remainingCount}
                      </div>
                    )}
                  </div>
                </div>
              </TooltipTrigger>
              <Portal>
                <TooltipContent
                  className="max-w-[300px]"
                  side="top"
                  avoidCollisions
                  collisionPadding={10}
                >
                  <div className="whitespace-pre-line">
                    Winner:{"\n"}
                    {winningCriteriaNames
                      .reduce<string[]>((acc, name, index) => {
                        if (index % 2 === 0) {
                          acc.push(name);
                        } else {
                          acc[acc.length - 1] += `, ${name}`;
                        }
                        if (index < winningCriteriaNames.length - 1) {
                          if (index % 2 === 1) {
                            acc[acc.length - 1] += ",";
                          }
                        }
                        return acc;
                      }, [])
                      .join("\n")}
                  </div>
                </TooltipContent>
              </Portal>
            </Tooltip>
          </TooltipProvider>
        )}
        <Media
          {...media}
          status={status}
          processingText={processingText}
          processingMsg={processingMsg}
        />
      </div>
    </>
  );
};

export default Body;
